// /services/turfai/workflowRegistry.ts

export interface WorkflowDefinition {
  name: string;
  version: string;
  description: string;
  type: 'single-stage' | 'multi-stage';
  stages?: string[];
  fallbackNames: string[];
  defaultTimeout?: number;
  retryPolicy?: {
    maxRetries: number;
    backoffMs: number;
  };
}

export const WORKFLOW_REGISTRY: Record<string, WorkflowDefinition> = {
  // System Workflows for DayOne Integration
  'system-classify': {
    name: 'turfai_classification',
    version: 'v1',
    type: 'single-stage',
    description: 'System workflow for document classification only',
    fallbackNames: ['turfai_classify', 'classification_only'],
    defaultTimeout: 30000,
    retryPolicy: { maxRetries: 3, backoffMs: 1000 }
  },
  'system-extract': {
    name: 'turfai_extraction',
    version: 'v1',
    type: 'single-stage',
    description: 'System workflow for document extraction only',
    fallbackNames: ['turfai_extract', 'extraction_only'],
    defaultTimeout: 45000,
    retryPolicy: { maxRetries: 2, backoffMs: 2000 }
  },
  'system-classify-extract': {
    name: 'turfai_classification_extraction',
    version: 'v1',
    type: 'single-stage',
    description: 'System workflow for classification and extraction',
    fallbackNames: ['turfai_classify_extract', 'classification_and_extraction'],
    defaultTimeout: 60000,
    retryPolicy: { maxRetries: 2, backoffMs: 3000 }
  },
  
  // Legacy Workflows (kept for backward compatibility)
  'sample-document-processing': { 
    name: 'Sample Document Processing Workflow',
    version: 'v1',
    type: 'single-stage',
    description: 'Sample workflow that processes documents through extraction and summarization',
    fallbackNames: ['sample_workflow', 'document_processing', 'sample'],
    defaultTimeout: 60000,
    retryPolicy: { maxRetries: 3, backoffMs: 1000 }
  },
  'extract-and-save': {
    name: 'Extract-and-Save-Workflow',
    version: 'v1', 
    type: 'single-stage',
    description: 'Extract text from documents and save to disk',
    fallbackNames: ['extract_save', 'extraction_workflow', 'extract'],
    defaultTimeout: 45000,
    retryPolicy: { maxRetries: 2, backoffMs: 1500 }
  },
  // Legacy mapping for classification (fallback to sample workflow)
  'document-classification': { 
    name: 'Sample Document Processing Workflow',
    version: 'v1',
    type: 'single-stage',
    description: 'AI document classification (mapped to sample workflow)',
    fallbackNames: ['document_classify', 'doc_classification', 'classify_v1'],
    defaultTimeout: 30000,
    retryPolicy: { maxRetries: 3, backoffMs: 1000 }
  },
  'data-extraction': {
    name: 'Extract-and-Save-Workflow', 
    version: 'v1',
    type: 'single-stage',
    description: 'Structured data extraction from documents (mapped to extract workflow)',
    fallbackNames: ['document_extract', 'data_extract', 'extract_v1'],
    defaultTimeout: 45000,
    retryPolicy: { maxRetries: 2, backoffMs: 2000 }
  },
  
  // Multi-Stage Workflows (Primary Production Workflows)
  'classify-and-extract': {
    name: 'classify_extract_pipeline',
    version: 'v1',
    type: 'multi-stage',
    stages: ['classify', 'extract', 'validate'],
    description: 'Complete document processing: classify → extract → validate',
    fallbackNames: ['full_pipeline', 'classify_extract', 'complete_processing'],
    defaultTimeout: 120000,
    retryPolicy: { maxRetries: 2, backoffMs: 5000 }
  },
  'extraction-pipeline': {
    name: 'extract_validate_pipeline',
    version: 'v1', 
    type: 'multi-stage',
    stages: ['extract', 'validate'],
    description: 'Data extraction with validation pipeline',
    fallbackNames: ['extract_pipeline', 'extraction_with_validation'],
    defaultTimeout: 90000,
    retryPolicy: { maxRetries: 2, backoffMs: 3000 }
  },
  
  // Collection-Based Workflows
  'collection-classification': {
    name: 'classify_collection',
    version: 'v1',
    type: 'single-stage',
    description: 'Batch classification of document collections',
    fallbackNames: ['batch_classify', 'collection_classify'],
    defaultTimeout: 180000,
    retryPolicy: { maxRetries: 1, backoffMs: 10000 }
  },
  'collection-extraction': {
    name: 'extract_collection',
    version: 'v1',
    type: 'single-stage',
    description: 'Batch extraction from document collections',
    fallbackNames: ['batch_extract', 'collection_extract'],
    defaultTimeout: 240000,
    retryPolicy: { maxRetries: 1, backoffMs: 15000 }
  }
};

// Environment-specific workflow mappings
export const WORKFLOW_ENVIRONMENTS = {
  development: { 
    'document-classification': 'classify_dev',
    'data-extraction': 'extract_dev',
    'document-validation': 'validate_dev',
    'classify-and-extract': 'classify_extract_pipeline_dev',
    'extraction-pipeline': 'extract_validate_pipeline_dev',
    'collection-classification': 'classify_collection_dev',
    'collection-extraction': 'extract_collection_dev'
  },
  staging: { 
    'document-classification': 'classify_staging',
    'data-extraction': 'extract_staging',
    'document-validation': 'validate_staging',
    'classify-and-extract': 'classify_extract_pipeline_staging',
    'extraction-pipeline': 'extract_validate_pipeline_staging',
    'collection-classification': 'classify_collection_staging',
    'collection-extraction': 'extract_collection_staging'
  },
  production: { 
    'document-classification': 'classify_prod',
    'data-extraction': 'extract_prod',
    'document-validation': 'validate_prod',
    'classify-and-extract': 'classify_extract_pipeline_prod',
    'extraction-pipeline': 'extract_validate_pipeline_prod',
    'collection-classification': 'classify_collection_prod',
    'collection-extraction': 'extract_collection_prod'
  }
};